import React, { useMemo, useCallback } from "react";
import {
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from "recharts";

interface PieChartData {
  labels: string[];
  datasets: Array<{
    label: string;
    data: number[];
    backgroundColor?: string[];
  }>;
}

interface PieChartProps {
  data: PieChartData;
  height?: number;
  showTooltip?: boolean;
  showLegend?: boolean;
}

const PieChart: React.FC<PieChartProps> = ({
  data,
  height = 300,
  showTooltip = true,
  showLegend = true,
}) => {
  const { labels, datasets } = data;
  const dataset = datasets[0]; // For MVP, we'll use the first dataset

  // Use colors from dataset if available, otherwise fallback to default colors
  const colors = dataset.backgroundColor || [
    "#3b82f6", // blue
    "#ef4444", // red
    "#10b981", // green
    "#f59e0b", // amber
    "#8b5cf6", // violet
    "#06b6d4", // cyan
  ];

  // Memoize chart data to prevent unnecessary re-renders
  const chartData = useMemo(
    () =>
      labels.map((label, index) => ({
        name: label,
        value: dataset.data[index],
      })),
    [labels, dataset.data]
  );

  const total = useMemo(
    () => chartData.reduce((sum, item) => sum + item.value, 0),
    [chartData]
  );

  // Memoize CustomTooltip to prevent re-creation on every render
  const CustomTooltip = useCallback(
    ({ active, payload }: any) => {
      if (active && payload && payload.length) {
        const data = payload[0];
        const percentage = ((data.value / total) * 100).toFixed(1);
        return (
          <div className="bg-white p-4 border border-gray-200 rounded-lg shadow-lg">
            <div className="space-y-2">
              <div className="text-sm font-medium text-gray-900">
                {data.name}
              </div>
              <div className="text-sm text-gray-600">
                Value:{" "}
                <span className="font-semibold text-gray-900">
                  {data.value}
                </span>
              </div>
              <div className="text-sm text-gray-600">
                Percentage:{" "}
                <span className="font-semibold text-gray-900">
                  {percentage}%
                </span>
              </div>
            </div>
          </div>
        );
      }
      return null;
    },
    [total]
  );

  // Memoize CustomLegend to prevent re-creation on every render
  const CustomLegend = useCallback(
    ({ payload }: any) => {
      if (!showLegend) return null;

      return (
        <div className="mt-4 grid grid-cols-2 gap-3">
          {payload.map((entry: any, index: number) => {
            // Find the corresponding data entry by name to get the correct value
            const dataEntry = chartData.find(
              (item) => item.name === entry.value
            );
            const value = dataEntry?.value || 0;

            return (
              <div key={index} className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded-full border border-gray-200"
                  style={{ backgroundColor: entry.color }}
                />
                <span className="text-sm text-gray-700">
                  {entry.value}: <span className="font-medium">{value}</span>
                </span>
              </div>
            );
          })}
        </div>
      );
    },
    [showLegend, chartData]
  );

  return (
    <div className="w-full">
      <div className="text-center mb-3">
        <h6 className="text-lg font-bold text-gray-900">{dataset.label}</h6>
      </div>

      <ResponsiveContainer width="100%" height={height}>
        <RechartsPieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            outerRadius={100}
            paddingAngle={0}
            dataKey="value"
            label={({ cx, cy, midAngle, outerRadius, value }) => {
              if (!midAngle || !outerRadius) return null;
              const RADIAN = Math.PI / 180;
              const radius = outerRadius * 0.6;
              const x = cx + radius * Math.cos(-midAngle * RADIAN);
              const y = cy + radius * Math.sin(-midAngle * RADIAN);

              return (
                <text
                  x={x}
                  y={y}
                  fill="white"
                  textAnchor="middle"
                  dominantBaseline="central"
                  className="text-sm font-bold"
                >
                  {value}
                </text>
              );
            }}
            labelLine={false}
          >
            {chartData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={colors[index % colors.length]}
                stroke="#ffffff"
                strokeWidth={2}
              />
            ))}
          </Pie>
          {showTooltip && <Tooltip content={<CustomTooltip />} />}
          {showLegend && <Legend content={<CustomLegend />} />}
        </RechartsPieChart>
      </ResponsiveContainer>

      {/* Center total */}
      <div className="text-center mt-3">
        <span className="text-sm text-gray-600 font-medium">
          Total: {total}
        </span>
      </div>
    </div>
  );
};

export default PieChart;
